import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import type { Control } from 'react-hook-form';
import type { ItineraryFormValues } from '../ItineraryForm';


interface TripDetailsFormProps {
    control: Control<ItineraryFormValues>;
}

export function TripDetailsForm({ control }: TripDetailsFormProps) {
  return (
    <>
        <FormField
            control={control}
            name="preferences"
            render={({ field }) => (
            <FormItem>
                <FormLabel>What are you looking for in a trip?</FormLabel>
                <FormControl>
                <Textarea
                    placeholder="e.g., 'A quiet, relaxing trip with short walks to the water. I love historical sites and beautiful scenery.'"
                    className="resize-none"
                    {...field}
                />
                </FormControl>
                <FormMessage />
            </FormItem>
            )}
        />
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <FormField
            control={control}
            name="skillLevel"
            render={({ field }) => (
                <FormItem>
                <FormLabel>Swimming Skill Level</FormLabel>
                <Select onValueChange={field.onChange} value={field.value} name={field.name}>
                    <FormControl>
                    <SelectTrigger>
                        <SelectValue placeholder="Select your skill level" />
                    </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                    </SelectContent>
                </Select>
                <FormMessage />
                </FormItem>
            )}
            />
            <FormField
            control={control}
            name="duration"
            render={({ field }) => (
                <FormItem>
                <FormLabel>Duration (days)</FormLabel>
                <FormControl>
                    <Input type="number" min="1" max="14" {...field} onChange={e => field.onChange(parseInt(e.target.value, 10))}/>
                </FormControl>
                <FormMessage />
                </FormItem>
            )}
            />
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <FormField
            control={control}
            name="location"
            render={({ field }) => (
                <FormItem>
                <FormLabel>Region of Interest</FormLabel>
                <FormControl>
                    <Input placeholder="e.g., Isle of Skye" {...field} />
                </FormControl>
                <FormMessage />
                </FormItem>
            )}
            />
            <FormField
            control={control}
            name="date"
            render={({ field }) => (
                <FormItem className="flex flex-col">
                <FormLabel>Start Date</FormLabel>
                <input type="hidden" name={field.name} value={field.value ? format(field.value, 'yyyy-MM-dd') : ''} />
                <Popover>
                    <PopoverTrigger asChild>
                    <FormControl>
                        <Button
                        variant="outline"
                        className={cn('pl-3 text-left font-normal', !field.value && 'text-muted-foreground')}
                        >
                        {field.value ? format(field.value, 'PPP') : <span>Pick a date</span>}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                    </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        disabled={(date) => date < new Date(new Date().setHours(0,0,0,0)) || date > new Date(new Date().setFullYear(new Date().getFullYear() + 1))}
                        initialFocus
                    />
                    </PopoverContent>
                </Popover>
                <FormMessage />
                </FormItem>
            )}
            />
        </div>
    </>
  );
}
